import React, { useState, useRef, Component } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, AlertTriangle, Eye, Search, Heart, MessageCircle, UserPlus, ThumbsDown, Target, Camera, Zap, TrendingUp, AlarmClockOff, RefreshCw, XCircle, Sparkles, Clock, MessageSquare, Repeat, BookOpen, ChevronRight, Wand2, Smartphone, Share2, Flame, Lightbulb, Tags, Monitor, Plus, Equal, Anchor, HeartHandshake, AlertCircle, HelpCircle, Gamepad2, Fingerprint, Quote, ArrowRight, PawPrint, Bird, Copy, FileText, Megaphone, Film, Smile, Link2, Ban, CheckCircle2, Rocket, Download, Apple, PlayCircle, Compass, Crown, UserCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
const topics = [{
  id: 'getting-started',
  name: 'Getting Started',
  description: 'Set up Pawchi and your Pawchi Plus access'
}, {
  id: 'account-warmup',
  name: 'Account Warmup',
  description: 'How to set up your TikTok account for success'
}, {
  id: 'viral-formula',
  name: 'The Viral Formula',
  description: 'The 4 organic formats that drive Pawchi growth'
}, {
  id: 'hooks',
  name: 'Hooks & Viral Angles',
  description: 'High-leverage hook patterns + comment bait'
}, {
  id: 'finch-pawchi',
  name: 'Finch → Pawchi Examples',
  description: 'Proven Finch UGC captions, remixed for Pawchi'
}, {
  id: 'ugc-brief',
  name: 'UGC Brief',
  description: 'The complete creative brief template for Pawchi videos'
}];
export function CreatorHelpCenter() {
  const [activeTopic, setActiveTopic] = useState('getting-started');
  const mainRef = useRef<HTMLElement>(null);
  function handleTopicSelect(topicId: string) {
    setActiveTopic(topicId);
    requestAnimationFrame(() => {
      mainRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    });
  }
  return <div className="min-h-screen bg-cream" data-id="element-185">
      <div className="max-w-7xl mx-auto px-6 pt-32 pb-20" data-id="element-186">
        <Link to="/creator" className="inline-flex items-center gap-2 text-warmBrown/60 hover:text-peach transition-colors mb-8 font-medium" data-id="element-187">
          <ArrowLeftIcon size={18} data-id="element-188" />
          Back to Creator Program
        </Link>

        <div className="mb-12 max-w-2xl" data-id="element-189">
          <div className="inline-flex items-center gap-2 bg-peach/10 text-peach px-3 py-1 rounded-full text-sm font-bold mb-4" data-id="element-190">
            <BookOpen size={14} data-id="element-191" />
            Creator Help Center
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-warmBrown mb-4 leading-tight" data-id="element-192">
            Everything you need to{' '}
            <span className="text-peach" data-id="element-193">create content that converts</span>
          </h1>
          <p className="text-warmBrown/80 text-lg leading-relaxed" data-id="element-194">
            Guides, protocols, and tips to help you grow your audience and earn
            more with Pawchi.
          </p>
        </div>

        <div className="grid md:grid-cols-[260px_1fr] gap-10" data-id="element-195">
          {/* Sidebar */}
          <aside data-id="element-196">
            <div className="sticky top-32" data-id="element-197">
              <h4 className="text-xs font-bold uppercase tracking-wider text-warmBrown/40 mb-4" data-id="element-198">
                Topics
              </h4>
              <nav className="space-y-1" data-id="element-199">
                {topics.map(topic => <button key={topic.id} onClick={() => handleTopicSelect(topic.id)} className={`w-full text-left px-4 py-3 rounded-xl flex items-center justify-between transition-all ${activeTopic === topic.id ? 'bg-peach text-white shadow-soft' : 'text-warmBrown hover:bg-white'}`} data-id="element-200">
                    <span className="font-medium" data-id="element-201">{topic.name}</span>
                    <ChevronRight size={16} className={activeTopic === topic.id ? 'opacity-100' : 'opacity-30'} data-id="element-202" />
                  </button>)}

                <div className="mt-6 p-4 bg-white rounded-xl border border-warmBrown/5" data-id="element-203">
                  <p className="text-xs text-warmBrown/60 leading-relaxed" data-id="element-204">
                    More guides coming soon. Have a topic you want covered?{' '}
                    <Link to="/contact-us" className="text-peach font-bold hover:underline" data-id="element-205">
                      Let us know
                    </Link>
                    .
                  </p>
                </div>
              </nav>
            </div>
          </aside>

          {/* Main content */}
          <main ref={mainRef} className="scroll-mt-32" data-id="element-206">
            {activeTopic === 'getting-started' && <GettingStartedTopic data-id="element-207" />}
            {activeTopic === 'account-warmup' && <AccountWarmupTopic data-id="element-208" />}
            {activeTopic === 'viral-formula' && <ViralFormulaTopic data-id="element-209" />}
            {activeTopic === 'hooks' && <HooksTopic data-id="element-210" />}
            {activeTopic === 'finch-pawchi' && <FinchPawchiTopic data-id="element-211" />}
            {activeTopic === 'ugc-brief' && <UgcBriefTopic data-id="element-212" />}
          </main>
        </div>
      </div>
    </div>;
}
function GettingStartedTopic() {
  const steps = [{
    number: 1,
    title: 'Download Pawchi',
    icon: <Download size={22} data-id="element-213" />,
    color: 'peach' as const,
    content: <>
          <p className="text-warmBrown/80 leading-relaxed mb-5" data-id="element-214">
            Pawchi is available on iOS and Android. Tap the matching badge for
            your phone to install.
          </p>
          <div className="flex flex-col sm:flex-row gap-3" data-id="element-215">
            <motion.a href="https://apps.apple.com/us/app/pawchi-self-care-pet/id6764858701" target="_blank" rel="noopener noreferrer" whileHover={{
          scale: 1.03
        }} whileTap={{
          scale: 0.97
        }} className="inline-block" data-id="element-216">
              <img src="https://cdn.magicpatterns.com/uploads/bgW7hVXTEHmConMeGXzro4/app-store.svg" alt="Download on the App Store" className="h-[52px] w-auto" data-id="element-217" />
            </motion.a>
            <motion.a href="https://play.google.com/store/apps/details?id=app.pawchi.mobile" target="_blank" rel="noopener noreferrer" whileHover={{
          scale: 1.03
        }} whileTap={{
          scale: 0.97
        }} className="inline-block -ml-3 sm:ml-0" data-id="element-218">
              <img src="https://cdn.magicpatterns.com/uploads/omQrJZxYBdQEgWZzBHqrye/play-store.png" alt="Get it on Google Play" className="h-[76px] w-auto -my-3" data-id="element-219" />
            </motion.a>
          </div>
        </>
  }, {
    number: 2,
    title: 'Create your Pawchi',
    icon: <PawPrint size={22} data-id="element-220" />,
    color: 'coral' as const,
    content: <>
          <p className="text-warmBrown/80 leading-relaxed mb-4" data-id="element-221">
            Upload a photo of your pet to transform them into your digital
            Pawchi companion. Pick a clear, well-lit shot for the best
            transformation.
          </p>
          <Callout color="peach" icon={<HeartHandshake size={16} data-id="element-223" />} title="Don't have a pet?" data-id="element-222">
            No problem! Use any pet photo you love, or{' '}
            <strong data-id="element-224">contact us</strong> and we'll assign one for you.
          </Callout>
          <Callout color="lavender" icon={<Sparkles size={16} data-id="element-226" />} title="Pro tip: make it unique" data-id="element-225">
            Add a <strong data-id="element-227">funny feature or accessory</strong> to your Pawchi —
            sunglasses, a tiny hat, a chef's apron, anything weird. A
            one-of-a-kind look is what makes viewers stop scrolling and comment.
          </Callout>
        </>
  }, {
    number: 3,
    title: 'Complete onboarding',
    icon: <CheckCircle2 size={22} data-id="element-228" />,
    color: 'lavender' as const,
    content: <>
          <p className="text-warmBrown/80 leading-relaxed mb-4" data-id="element-229">
            Walk through the onboarding flow inside the app. This sets up your
            habits, mood tracker, and connects everything to your new Pawchi.
          </p>
          <Callout color="coral" icon={<Film size={16} data-id="element-231" />} title="Hit record before you start" data-id="element-230">
            Screen-record your onboarding so you can{' '}
            <strong data-id="element-232">capture the emotional transformation</strong> from your real
            pet to the digitized Pawchi. That reveal moment is your most viral
            asset — you only get one first reaction, so don't miss it.
          </Callout>
        </>
  }, {
    number: 4,
    title: 'Explore the app',
    icon: <Compass size={22} data-id="element-233" />,
    color: 'sage' as const,
    content: <p className="text-warmBrown/80 leading-relaxed" data-id="element-234">
          Tap around. Get comfortable with how habits, the mood log, breathing
          exercises, and your Pawchi's reactions all fit together. The more
          familiar you are, the more genuine your videos will feel.
        </p>
  }, {
    number: 5,
    title: 'Unlock Pawchi Plus',
    icon: <Crown size={22} data-id="element-235" />,
    color: 'peach' as const,
    content: <>
          <p className="text-warmBrown/80 leading-relaxed mb-4" data-id="element-236">
            Once you've explored the app,{' '}
            <strong className="text-warmBrown" data-id="element-237">let us know</strong> — we'll
            grant you Pawchi Plus, which unlocks all animations and premium
            features so your content can showcase the full experience.
          </p>
          <Callout color="lavender" icon={<Sparkles size={16} data-id="element-239" />} title="Need more Pawchis?" data-id="element-238">
            If you want to feature additional pets in your content, contact us
            for <strong data-id="element-240">extra credits</strong>.
          </Callout>
        </>
  }];
  const colorMap = {
    peach: {
      bg: 'bg-peach/10',
      text: 'text-peach',
      solid: 'bg-peach'
    },
    coral: {
      bg: 'bg-coral/10',
      text: 'text-coral',
      solid: 'bg-coral'
    },
    lavender: {
      bg: 'bg-lavender/10',
      text: 'text-lavender',
      solid: 'bg-lavender'
    },
    sage: {
      bg: 'bg-sage/10',
      text: 'text-sage',
      solid: 'bg-sage'
    }
  };
  return <motion.article initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4
  }} className="space-y-10" data-id="element-241">
      {/* Intro */}
      <header data-id="element-242">
        <div className="inline-flex items-center gap-2 bg-sage/10 text-sage px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4" data-id="element-243">
          <Rocket size={14} data-id="element-244" />
          Start Here
        </div>
        <h2 className="text-3xl md:text-4xl font-black text-warmBrown mb-4" data-id="element-245">
          Getting Started with Pawchi
        </h2>
        <p className="text-warmBrown/80 text-lg leading-relaxed" data-id="element-246">
          Welcome to the Pawchi Creator Program! Follow these{' '}
          <strong className="text-warmBrown" data-id="element-247">5 quick steps</strong> to get set
          up with the app, unlock Pawchi Plus, and start creating content.
        </p>
      </header>

      {/* What is Pawchi */}
      <section className="bg-gradient-to-br from-peach to-coral rounded-3xl p-8 md:p-10 text-white" data-id="element-248">
        <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4" data-id="element-249">
          <PawPrint size={14} data-id="element-250" />
          What is Pawchi?
        </div>
        <p className="text-xl md:text-2xl font-bold leading-snug mb-3" data-id="element-251">
          Pawchi turns your real pet into a cute digital companion.
        </p>
        <p className="text-white/90 leading-relaxed" data-id="element-252">
          Keep them happy by completing daily habits, tracking your mood, and
          taking moments to breathe. Self-care, gamified — and powered by the
          pet you actually love.
        </p>
      </section>

      {/* Steps */}
      <section className="space-y-5" data-id="element-253">
        {steps.map(step => {
        const c = colorMap[step.color];
        return <article key={step.number} className="bg-white rounded-3xl p-8 shadow-soft border border-warmBrown/5" data-id="element-254">
              <div className="flex items-start gap-4 mb-5" data-id="element-255">
                <div className={`w-14 h-14 rounded-2xl ${c.solid} text-white flex items-center justify-center flex-shrink-0 font-black text-xl`} data-id="element-256">
                  {step.number}
                </div>
                <div className="flex-1 min-w-0" data-id="element-257">
                  <div className="flex items-center gap-2 mb-1" data-id="element-258">
                    <span className={c.text} data-id="element-259">{step.icon}</span>
                    <p className={`text-xs font-bold uppercase tracking-wider ${c.text}`} data-id="element-260">
                      Step {step.number}
                    </p>
                  </div>
                  <h3 className="text-2xl font-black text-warmBrown" data-id="element-261">
                    {step.title}
                  </h3>
                </div>
              </div>
              <div data-id="element-262">{step.content}</div>
            </article>;
      })}
      </section>

      {/* Contact us */}
      <section className="bg-warmBrown rounded-3xl p-8 md:p-10 text-cream" data-id="element-263">
        <div className="flex items-start gap-4 mb-4" data-id="element-264">
          <div className="w-12 h-12 rounded-full bg-peach/20 text-peach flex items-center justify-center flex-shrink-0" data-id="element-265">
            <UserCircle2 size={22} data-id="element-266" />
          </div>
          <div data-id="element-267">
            <h3 className="text-2xl font-black mb-1" data-id="element-268">Need help? Contact us</h3>
            <p className="text-cream/60 text-sm" data-id="element-269">
              For Pawchi Plus access, extra credits, or anything you get stuck
              on.
            </p>
          </div>
        </div>
        <Link to="/contact-us" className="inline-flex items-center gap-2 px-6 py-3 bg-peach hover:bg-peach/90 text-white rounded-full font-bold transition-colors mt-4" data-id="element-270">
          Get in touch
          <ArrowRight size={16} data-id="element-271" />
        </Link>
      </section>

      {/* Next steps */}
      <Callout color="sage" icon={<Lightbulb size={18} data-id="element-273" />} title="Once you're set up" data-id="element-272">
        Jump to <strong data-id="element-274">Account Warmup</strong> next to prep your TikTok or
        Instagram account before posting your first Pawchi video. The warmup is
        the #1 reason new creators see early traction.
      </Callout>
    </motion.article>;
}
function AccountWarmupTopic() {
  return <motion.article initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4
  }} className="space-y-10" data-id="element-275">
      {/* Intro */}
      <header data-id="element-276">
        <div className="inline-flex items-center gap-2 bg-coral/10 text-coral px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4" data-id="element-277">
          <AlertTriangle size={14} data-id="element-278" />
          Non-Negotiable
        </div>
        <h2 className="text-3xl md:text-4xl font-black text-warmBrown mb-4" data-id="element-279">
          Account Warmup Protocol
        </h2>
        <p className="text-warmBrown/80 text-lg leading-relaxed" data-id="element-280">
          Starting a new account without warmup is the{' '}
          <strong className="text-warmBrown" data-id="element-281">
            #1 reason first videos flop
          </strong>
          . TikTok needs behavioral signal before it knows who to show your
          content to.
        </p>
      </header>

      {/* Step 0: Handle Setup */}
      <section className="bg-white rounded-3xl p-8 md:p-10 shadow-soft border border-warmBrown/5" data-id="element-282">
        <div className="flex items-start gap-4 mb-5" data-id="element-283">
          <div className="w-14 h-14 rounded-2xl bg-warmBrown text-cream flex items-center justify-center flex-shrink-0 font-black text-xl" data-id="element-284">
            0
          </div>
          <div className="flex-1 min-w-0" data-id="element-285">
            <p className="text-xs font-bold uppercase tracking-wider text-warmBrown/40 mb-1" data-id="element-286">
              Before You Begin
            </p>
            <h3 className="text-2xl font-black text-warmBrown" data-id="element-287">
              Set Up Your Handles
            </h3>
            <p className="text-warmBrown/60 text-sm mt-1" data-id="element-288">
              Lock in your TikTok and Instagram handles before posting day 1.
            </p>
          </div>
        </div>

        <div className="space-y-5" data-id="element-289">
          <div data-id="element-290">
            <p className="text-xs font-bold uppercase tracking-wider text-peach mb-2" data-id="element-291">
              Step 1 · Pick a handle
            </p>
            <p className="text-warmBrown/80 leading-relaxed mb-3" data-id="element-292">
              Choose the same handle for TikTok and Instagram. We recommend{' '}
              <strong className="text-warmBrown" data-id="element-293">your name + pet name</strong>{' '}
              or something{' '}
              <strong className="text-warmBrown" data-id="element-294">
                wellness / self-care related
              </strong>
              .
            </p>

            <p className="text-xs font-bold uppercase tracking-wider text-warmBrown/40 mb-2" data-id="element-295">
              Examples
            </p>
            <div className="flex flex-wrap gap-2" data-id="element-296">
              {['@miguelandpolo', '@dailypolo', '@dailylifeofatlas', '@atlasselfcare'].map(handle => <span key={handle} className="px-3 py-1.5 bg-peach/10 text-peach rounded-lg font-bold text-sm border border-peach/20" data-id="element-297">
                  {handle}
                </span>)}
            </div>
          </div>

          <div data-id="element-298">
            <p className="text-xs font-bold uppercase tracking-wider text-peach mb-2" data-id="element-299">
              Step 2 · Send us your info
            </p>
            <p className="text-warmBrown/80 leading-relaxed mb-3" data-id="element-300">
              Once you've picked your handle, send us:
            </p>
            <ul className="space-y-2" data-id="element-301">
              <li className="flex items-start gap-3 text-warmBrown/80" data-id="element-302">
                <CheckCircle2 size={18} className="text-peach mt-0.5 flex-shrink-0" data-id="element-303" />
                Your{' '}
                <strong className="text-warmBrown" data-id="element-304">
                  chosen TikTok & Instagram handle
                </strong>
              </li>
              <li className="flex items-start gap-3 text-warmBrown/80" data-id="element-305">
                <CheckCircle2 size={18} className="text-peach mt-0.5 flex-shrink-0" data-id="element-306" />
                Your{' '}
                <strong className="text-warmBrown" data-id="element-307">
                  personal email address
                </strong>
              </li>
            </ul>
          </div>

          <div data-id="element-308">
            <p className="text-xs font-bold uppercase tracking-wider text-peach mb-2" data-id="element-309">
              Step 3 · Get your creator email
            </p>
            <p className="text-warmBrown/80 leading-relaxed" data-id="element-310">
              We'll send you back a{' '}
              <strong className="text-warmBrown" data-id="element-311">
                dedicated email address
              </strong>{' '}
              to use when signing up on TikTok and Instagram. This keeps your
              creator accounts clean and separate from your personal inbox.
            </p>
          </div>

          <div data-id="element-312">
            <p className="text-xs font-bold uppercase tracking-wider text-peach mb-2" data-id="element-313">
              Step 4 · Sign up and set up your accounts
            </p>
            <p className="text-warmBrown/80 leading-relaxed mb-3" data-id="element-314">
              Use the{' '}
              <strong className="text-warmBrown" data-id="element-315">dedicated email</strong> we
              sent you to create your TikTok and Instagram accounts under your
              chosen handle. Then fill out the profile:
            </p>
            <ul className="space-y-2 mb-4" data-id="element-316">
              <li className="flex items-start gap-3 text-warmBrown/80" data-id="element-317">
                <CheckCircle2 size={18} className="text-peach mt-0.5 flex-shrink-0" data-id="element-318" />
                Use a{' '}
                <strong className="text-warmBrown" data-id="element-319">
                  genuine, authentic photo of you and your pet
                </strong>{' '}
                as your profile picture
              </li>
              <li className="flex items-start gap-3 text-warmBrown/80" data-id="element-320">
                <CheckCircle2 size={18} className="text-peach mt-0.5 flex-shrink-0" data-id="element-321" />
                Write a short, friendly bio that hints at the self-care + pet
                vibe
              </li>
              <li className="flex items-start gap-3 text-warmBrown/80" data-id="element-322">
                <CheckCircle2 size={18} className="text-peach mt-0.5 flex-shrink-0" data-id="element-323" />
                Keep the same handle, photo, and bio consistent across TikTok
                and Instagram
              </li>
            </ul>
            <Callout color="sage" icon={<Heart size={16} data-id="element-325" />} title="Why authentic matters" data-id="element-324">
              Real-you-with-real-pet photos build trust instantly. Stock images,
              AI portraits, or pet-only photos look like a brand account — which
              kills the personal-discovery feel that drives conversions.
            </Callout>
          </div>

          <Link to="/contact-us" className="inline-flex items-center gap-2 px-6 py-3 bg-peach hover:bg-peach/90 text-white rounded-full font-bold transition-colors" data-id="element-326">
            Send your handle
            <ArrowRight size={16} data-id="element-327" />
          </Link>
        </div>
      </section>

      {/* Days 1-2 */}
      <PhaseCard phase="Days 1–2" title="Pure Observer Mode" color="lavender" icon={<Eye size={24} data-id="element-329" />} data-id="element-328">
        <div className="space-y-4" data-id="element-330">
          <Rule icon={<XCircle size={18} data-id="element-332" />} negative data-id="element-331">
            Do <strong data-id="element-333">NOT</strong> post anything
          </Rule>
          <Rule icon={<Clock size={18} data-id="element-335" />} data-id="element-334">
            Open app <strong data-id="element-336">2× per day</strong>, 10 minutes each session
          </Rule>
          <Rule icon={<Search size={18} data-id="element-338" />} data-id="element-337">
            Search your exact niche:{' '}
            <em className="text-warmBrown/70" data-id="element-339">
              "pet care routine," "self-care habits," "tamagotchi," "virtual
              pet," "cat routine," "dog self care"
            </em>
          </Rule>
          <Rule icon={<Eye size={18} data-id="element-341" />} data-id="element-340">
            Watch every video <strong data-id="element-342">TO THE END</strong> — partial watches are
            negative signals
          </Rule>
          <Rule icon={<Heart size={18} data-id="element-344" />} data-id="element-343">
            Like <strong data-id="element-345">20–30 niche videos</strong> per day
          </Rule>
          <Rule icon={<MessageCircle size={18} data-id="element-347" />} data-id="element-346">
            Leave <strong data-id="element-348">5 genuine comments</strong> (full sentences, not
            emojis or one-word replies)
          </Rule>
          <Rule icon={<UserPlus size={18} data-id="element-350" />} data-id="element-349">
            Follow <strong data-id="element-351">3–5 relevant accounts</strong>
          </Rule>
          <Rule icon={<ThumbsDown size={18} data-id="element-353" />} data-id="element-352">
            Aggressively tap <strong data-id="element-354">"Not Interested"</strong> on anything
            off-niche
          </Rule>
        </div>

        <Callout color="sage" icon={<Target size={18} data-id="element-356" />} title="Goal by Day 2" data-id="element-355">
          Your For You Page shows pet content, self-care content, habit apps,
          and cute animal transformations. If it doesn't,{' '}
          <strong data-id="element-357">you moved too fast</strong>.
        </Callout>
      </PhaseCard>

      {/* Engagement Mix */}
      <section className="bg-white rounded-3xl p-8 md:p-10 shadow-soft border border-warmBrown/5" data-id="element-358">
        <div className="flex items-start gap-4 mb-6" data-id="element-359">
          <div className="w-12 h-12 rounded-full bg-peach/10 text-peach flex items-center justify-center flex-shrink-0" data-id="element-360">
            <Target size={22} data-id="element-361" />
          </div>
          <div data-id="element-362">
            <h3 className="text-2xl font-black text-warmBrown mb-1" data-id="element-363">
              Your Engagement Mix
            </h3>
            <p className="text-warmBrown/60 text-sm" data-id="element-364">
              The exact ratio of content to watch, like, and comment on during
              warmup.
            </p>
          </div>
        </div>

        <div className="space-y-4" data-id="element-365">
          <EngagementBucket percent={40} label="Pets" color="peach" items={['Funny dog videos', 'Pet POV content', 'Pet-owner relatable content', 'Cute animal clips', 'Pet transformations']} goal="Tell TikTok you're interested in pet owners." data-id="element-366" />
          <EngagementBucket percent={40} label="Self-Care" color="lavender" items={['Water intake', 'Morning routines', 'Habit tracking', 'Self-improvement', 'Wellness content']} goal="Tell TikTok you're interested in people actively trying to improve themselves." data-id="element-367" />
          <EngagementBucket percent={20} label="Productivity / Mental Health" color="sage" items={['Atomic Habits', 'Motivation', 'Journaling', 'ADHD productivity', '"Get your life together" content']} goal="Reach the audience that actually installs apps." data-id="element-368" />
        </div>
      </section>

      {/* Day 3 */}
      <PhaseCard phase="Day 3" title="Diagnostic Posting" color="peach" icon={<Camera size={24} data-id="element-370" />} data-id="element-369">
        <p className="text-warmBrown/80 leading-relaxed mb-4" data-id="element-371">
          Before posting real content, post <strong data-id="element-372">2 slideshows</strong> (low
          production effort). These are diagnostic tools.
        </p>

        <div className="bg-cream/60 rounded-2xl p-6 mb-4" data-id="element-373">
          <p className="font-bold text-warmBrown mb-3" data-id="element-374">
            A slideshow "passed" if within 5 hours it gets:
          </p>
          <ul className="space-y-2" data-id="element-375">
            <li className="flex items-center gap-3 text-warmBrown/80" data-id="element-376">
              <span className="w-2 h-2 rounded-full bg-peach" data-id="element-377"></span>
              <strong className="text-warmBrown" data-id="element-378">500+</strong> impressions
            </li>
            <li className="flex items-center gap-3 text-warmBrown/80" data-id="element-379">
              <span className="w-2 h-2 rounded-full bg-peach" data-id="element-380"></span>
              <strong className="text-warmBrown" data-id="element-381">20–30+</strong> likes
            </li>
          </ul>
        </div>

        <div className="grid sm:grid-cols-2 gap-3" data-id="element-382">
          <div className="bg-sage/10 border border-sage/20 rounded-xl p-4" data-id="element-383">
            <p className="text-sm font-bold text-sage mb-1" data-id="element-384">✓ If it passes</p>
            <p className="text-sm text-warmBrown/80" data-id="element-385">
              Post up to 3 main videos that same day.
            </p>
          </div>
          <div className="bg-coral/10 border border-coral/20 rounded-xl p-4" data-id="element-386">
            <p className="text-sm font-bold text-coral mb-1" data-id="element-387">✗ If it fails</p>
            <p className="text-sm text-warmBrown/80" data-id="element-388">
              Do one more day of observer mode. Your account is still cold.
            </p>
          </div>
        </div>
      </PhaseCard>

      {/* Day 4+ */}
      <PhaseCard phase="Day 4+" title="Active Posting" color="sage" icon={<Zap size={24} data-id="element-390" />} data-id="element-389">
        <div className="space-y-4" data-id="element-391">
          <Rule icon={<Camera size={18} data-id="element-393" />} data-id="element-392">
            Post <strong data-id="element-394">1–2 videos per day</strong>
          </Rule>
          <Rule icon={<Repeat size={18} data-id="element-396" />} data-id="element-395">
            Continue the daily feed engagement routine (10 min, 2×/day) —{' '}
            <strong data-id="element-397">this is not optional</strong>
          </Rule>
          <Rule icon={<TrendingUp size={18} data-id="element-399" />} data-id="element-398">
            Watch performance and adjust format based on what's holding viewers
          </Rule>
        </div>
      </PhaseCard>

      {/* Abandonment Rule */}
      <section className="bg-warmBrown rounded-3xl p-8 md:p-10 text-cream" data-id="element-400">
        <div className="flex items-start gap-4 mb-4" data-id="element-401">
          <div className="w-12 h-12 rounded-full bg-coral/20 text-coral flex items-center justify-center flex-shrink-0" data-id="element-402">
            <AlarmClockOff size={22} data-id="element-403" />
          </div>
          <div data-id="element-404">
            <h3 className="text-2xl font-black mb-1" data-id="element-405">
              Account Abandonment Rule
            </h3>
            <p className="text-cream/60 text-sm" data-id="element-406">
              When to cut your losses and restart.
            </p>
          </div>
        </div>

        <div className="space-y-4 text-cream/80 leading-relaxed" data-id="element-407">
          <p data-id="element-408">
            If <strong className="text-cream" data-id="element-409">6 consecutive videos</strong> stay
            under 300 views: post 3 videos recorded natively inside TikTok (no
            external edits, no CapCut). Sometimes this resets the trust signal.
          </p>
          <p data-id="element-410">
            If still nothing after those 3 videos:{' '}
            <strong className="text-cream" data-id="element-411">abandon the account</strong>. Restart
            costs 2 days. Staying on a dead account costs weeks.
          </p>
        </div>
      </section>

      {/* Retention Benchmark */}
      <section className="bg-gradient-to-br from-peach to-coral rounded-3xl p-8 md:p-10 text-white" data-id="element-412">
        <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4" data-id="element-413">
          <TrendingUp size={14} data-id="element-414" />
          Most Important Metric
        </div>
        <h3 className="text-3xl font-black mb-4" data-id="element-415">The Retention Benchmark</h3>
        <p className="text-white/90 leading-relaxed mb-6" data-id="element-416">
          TikTok distributes in waves. A video gets shown to a small test batch.
          If they watch to the end, it gets shown to a bigger batch.{' '}
          <strong className="text-white" data-id="element-417">This compounds.</strong>
        </p>

        <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-white/20" data-id="element-418">
          <p className="text-sm uppercase tracking-wider text-white/70 font-bold mb-2" data-id="element-419">
            Target
          </p>
          <p className="text-4xl md:text-5xl font-black mb-2" data-id="element-420">42%+</p>
          <p className="text-white/90" data-id="element-421">full video completion rate</p>
          <p className="text-sm text-white/70 mt-3" data-id="element-422">
            Below 42%, distribution waves stop. Above it, they compound.
          </p>
        </div>

        <h4 className="font-bold mb-3 text-white" data-id="element-423">Practical implications:</h4>
        <ul className="space-y-3 mb-6" data-id="element-424">
          <li className="flex items-start gap-3 text-white/90" data-id="element-425">
            <span className="font-black text-white" data-id="element-426">1.</span>
            <span data-id="element-427">
              First <strong className="text-white" data-id="element-428">3 seconds must hook</strong>{' '}
              (stop the scroll)
            </span>
          </li>
          <li className="flex items-start gap-3 text-white/90" data-id="element-429">
            <span className="font-black text-white" data-id="element-430">2.</span>
            <span data-id="element-431">
              Middle must move{' '}
              <strong className="text-white" data-id="element-432">fast enough</strong> that dropping
              off feels like a loss
            </span>
          </li>
          <li className="flex items-start gap-3 text-white/90" data-id="element-433">
            <span className="font-black text-white" data-id="element-434">3.</span>
            <span data-id="element-435">
              Last <strong className="text-white" data-id="element-436">3 seconds must earn</strong>{' '}
              the watch (payoff or pattern interrupt)
            </span>
          </li>
        </ul>

        <p className="text-white/90 leading-relaxed mb-4" data-id="element-437">
          Slow middles kill videos that have great hooks.{' '}
          <strong className="text-white" data-id="element-438">Cut ruthlessly.</strong>
        </p>
        <p className="text-white/80 text-sm leading-relaxed" data-id="element-439">
          Likes and comments matter, but only as downstream effects of
          retention. Retention is the root signal.
        </p>

        <div className="mt-6 pt-6 border-t border-white/20" data-id="element-440">
          <p className="text-sm uppercase tracking-wider text-white/70 font-bold mb-2" data-id="element-441">
            3-Second Watch-Time Benchmark
          </p>
          <p className="text-white/90 leading-relaxed" data-id="element-442">
            <strong className="text-white" data-id="element-443">75–80%</strong> of viewers watching
            past the first 3 seconds = "viral-ready" format. Use this as the
            primary filter for what formats to scale.
          </p>
        </div>
      </section>

      {/* Daily Maintenance */}
      <PhaseCard phase="Daily" title="Maintenance (Forever)" color="lavender" icon={<Repeat size={24} data-id="element-445" />} data-id="element-444">
        <p className="text-warmBrown/80 leading-relaxed" data-id="element-446">
          Warming up is not a one-time event. If you stop behaving like a human
          on the app (watching, liking, commenting), the account goes cold and
          performance drops.{' '}
          <strong className="text-warmBrown" data-id="element-447">
            Treat daily feed engagement like brushing teeth.
          </strong>
        </p>
      </PhaseCard>

      {/* Comment Seeding */}
      <section className="bg-white rounded-3xl p-8 md:p-10 shadow-soft border border-warmBrown/5" data-id="element-448">
        <div className="flex items-start gap-4 mb-6" data-id="element-449">
          <div className="w-12 h-12 rounded-full bg-peach/10 text-peach flex items-center justify-center flex-shrink-0" data-id="element-450">
            <MessageSquare size={22} data-id="element-451" />
          </div>
          <div data-id="element-452">
            <h3 className="text-2xl font-black text-warmBrown mb-1" data-id="element-453">
              Comment Seeding
            </h3>
            <p className="text-warmBrown/60 text-sm" data-id="element-454">
              Turning curiosity into conversions.
            </p>
          </div>
        </div>

        <p className="text-warmBrown/80 leading-relaxed mb-6" data-id="element-455">
          When a video gains traction but isn't converting, the issue is often a{' '}
          <strong className="text-warmBrown" data-id="element-456">
            lack of visible social proof
          </strong>{' '}
          in comments. Seed comments from secondary accounts.
        </p>

        <h4 className="font-bold text-warmBrown mb-3" data-id="element-457">
          Effective seed comments:
        </h4>
        <div className="space-y-2 mb-6" data-id="element-458">
          {['what app is this??', 'does this actually work?', 'omg I need to try this with my cat', 'my dog would literally be perfect for this', 'anyone used this consistently? results?'].map(comment => <div key={comment} className="bg-cream/60 rounded-xl px-4 py-3 text-warmBrown/80 italic border-l-4 border-peach/40" data-id="element-459">
              "{comment}"
            </div>)}
        </div>

        <Callout color="sage" icon={<Sparkles size={18} data-id="element-461" />} title="Pro tip" data-id="element-460">
          Give Claude the hook of the video + your ICP + Pawchi description →
          generate an organic-sounding comment where the persona explains how
          the app helped them. This turns passive viewers into curious readers
          who then convert.
        </Callout>

        <p className="text-warmBrown font-bold mt-6 text-center" data-id="element-462">
          No comments = no curiosity = no conversions.
        </p>
      </section>
    </motion.article>;
}
function ViralFormulaTopic() {
  const ingredients = [{
    label: 'Viral Feature',
    title: 'Real pet photo → animated AI sprite',
    desc: 'A "reveal moment" — identical in structure to face ratings, food scanners, or body analysis apps that generate millions of views. The reveal IS the ad.',
    icon: <Wand2 size={22} data-id="element-463" />,
    color: 'peach' as const
  }, {
    label: 'Core Desire',
    title: 'Love + identity + nostalgia',
    desc: 'Love for a real pet, desire for a self-care identity, and Tamagotchi nostalgia — three of the strongest emotional pulls online right now.',
    icon: <Heart size={22} data-id="element-464" />,
    color: 'coral' as const
  }, {
    label: 'Shareable Output',
    title: 'The animated pet sprite',
    desc: "The animated pet sprite, the pet's 'stats,' and the transformation itself — all inherently shareable across stories, group chats, and feeds.",
    icon: <Share2 size={22} data-id="element-465" />,
    color: 'lavender' as const
  }];
  const formats = [{
    number: 1,
    priority: 'Highest Priority',
    title: 'The Transformation Reveal',
    structure: 'Real pet photo (before) → dramatic transition → animated Pawchi sprite (after)',
    hooks: ['"I turned my cat into a Tamagotchi... [she has opinions]"', '"POV you upload your dog\'s photo to this app"', '"This AI turned my actual cat into an animated version of herself and I\'m not okay"'],
    why: 'The reveal creates genuine delight and "I want to see mine" impulse. The comment section fills with "do mine!" requests organically. Highest-conversion format.',
    icon: <Wand2 size={22} data-id="element-466" />,
    color: 'peach' as const
  }, {
    number: 2,
    priority: 'Self-Care Angle',
    title: 'The Accountability Loop',
    structure: 'Morning routine or habit check-in, showing how completing habits makes the pet thrive',
    hooks: ['"My virtual pet died because I didn\'t drink water [I deserved it]"', '"Day 14 of letting my pet motivate my self-care routine"', '"What happens to your Pawchi when you skip your habits for 3 days"'],
    why: 'Demonstrates the core gameplay loop and the emotional stakes (pet declining hits hard). Creates relatability for anyone who struggles with habits.',
    icon: <Heart size={22} data-id="element-467" />,
    color: 'coral' as const
  }, {
    number: 3,
    priority: 'Pet Identity',
    title: 'Slideshows',
    structure: '"If you\'re the kind of person who [trait]... your pet would be a [species/type]" — or — "What your self-care score says about your pet\'s personality"',
    hooks: [],
    why: 'Identity labeling creates instant engagement. People tag friends, share to stories, comment with their own results.',
    icon: <Tags size={22} data-id="element-468" />,
    color: 'lavender' as const
  }, {
    number: 4,
    priority: 'App Demo',
    title: 'Screen Recording Demo',
    structure: 'Screen record of onboarding / pet customization with voiceover or text captions',
    hooks: ['"Okay this app genuinely got me to drink water for the first time in a week"'],
    why: "Shows the product in the most authentic, low-production way. Feels user-generated even when it isn't.",
    icon: <Monitor size={22} data-id="element-469" />,
    color: 'sage' as const
  }];
  const colorMap = {
    peach: {
      bg: 'bg-peach/10',
      text: 'text-peach',
      solid: 'bg-peach'
    },
    coral: {
      bg: 'bg-coral/10',
      text: 'text-coral',
      solid: 'bg-coral'
    },
    lavender: {
      bg: 'bg-lavender/10',
      text: 'text-lavender',
      solid: 'bg-lavender'
    },
    sage: {
      bg: 'bg-sage/10',
      text: 'text-sage',
      solid: 'bg-sage'
    }
  };
  return <motion.article initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4
  }} className="space-y-10" data-id="element-470">
      {/* Intro */}
      <header data-id="element-471">
        <div className="inline-flex items-center gap-2 bg-peach/10 text-peach px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4" data-id="element-472">
          <Flame size={14} data-id="element-473" />
          Growth Strategy
        </div>
        <h2 className="text-3xl md:text-4xl font-black text-warmBrown mb-4" data-id="element-474">
          The Viral Formula for Pawchi
        </h2>
        <p className="text-warmBrown/80 text-lg leading-relaxed" data-id="element-475">
          Every viral product on TikTok and Reels follows the same recipe.{' '}
          <strong className="text-warmBrown" data-id="element-476">
            Pawchi has all three ingredients.
          </strong>{' '}
          Here's how to use them.
        </p>
      </header>

      {/* Formula equation */}
      <section className="bg-white rounded-3xl p-8 md:p-10 shadow-soft border border-warmBrown/5" data-id="element-477">
        <p className="text-xs font-bold uppercase tracking-wider text-warmBrown/40 mb-6" data-id="element-478">
          The Formula
        </p>
        <div className="flex flex-wrap items-center gap-3 md:gap-4 text-warmBrown" data-id="element-479">
          <span className="px-4 py-2 bg-peach/10 text-peach rounded-xl font-bold text-sm md:text-base" data-id="element-480">
            Viral Feature
          </span>
          <Plus size={18} className="text-warmBrown/40" data-id="element-481" />
          <span className="px-4 py-2 bg-coral/10 text-coral rounded-xl font-bold text-sm md:text-base" data-id="element-482">
            Core Human Desire
          </span>
          <Plus size={18} className="text-warmBrown/40" data-id="element-483" />
          <span className="px-4 py-2 bg-lavender/10 text-lavender rounded-xl font-bold text-sm md:text-base" data-id="element-484">
            Shareable Output
          </span>
          <Equal size={18} className="text-warmBrown/40" data-id="element-485" />
          <span className="px-4 py-2 bg-warmBrown text-cream rounded-xl font-bold text-sm md:text-base" data-id="element-486">
            Organic Growth Machine
          </span>
        </div>
      </section>

      {/* Three ingredients */}
      <section data-id="element-487">
        <h3 className="text-2xl font-black text-warmBrown mb-6" data-id="element-488">
          How Pawchi checks every box
        </h3>
        <div className="grid md:grid-cols-3 gap-4" data-id="element-489">
          {ingredients.map(item => {
          const c = colorMap[item.color];
          return <div key={item.label} className="bg-white rounded-3xl p-6 shadow-soft border border-warmBrown/5 flex flex-col" data-id="element-490">
                <div className={`w-12 h-12 rounded-full ${c.bg} ${c.text} flex items-center justify-center mb-4`} data-id="element-491">
                  {item.icon}
                </div>
                <p className={`text-xs font-bold uppercase tracking-wider ${c.text} mb-1`} data-id="element-492">
                  {item.label}
                </p>
                <h4 className="font-black text-warmBrown mb-2 leading-tight" data-id="element-493">
                  {item.title}
                </h4>
                <p className="text-sm text-warmBrown/70 leading-relaxed" data-id="element-494">
                  {item.desc}
                </p>
              </div>;
        })}
        </div>
      </section>

      {/* Reveal mechanic */}
      <section className="bg-gradient-to-br from-peach to-coral rounded-3xl p-8 md:p-10 text-white" data-id="element-495">
        <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4" data-id="element-496">
          <Sparkles size={14} data-id="element-497" />
          Highest Priority
        </div>
        <h3 className="text-3xl font-black mb-4" data-id="element-498">The Reveal Mechanic</h3>
        <p className="text-white/90 leading-relaxed mb-6" data-id="element-499">
          The single most viral moment in Pawchi is the transformation: user
          uploads real pet photo → Pawchi generates animated sprite. This is
          your{' '}
          <strong className="text-white" data-id="element-500">UMax "face rating reveal,"</strong>{' '}
          your <strong className="text-white" data-id="element-501">Cal AI "calorie reveal."</strong>
        </p>
        <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-6 border border-white/20 mb-6" data-id="element-502">
          <p className="font-bold text-lg leading-snug" data-id="element-503">
            Every ad creative, every organic video, every influencer brief must
            center on this reveal.{' '}
            <span className="block mt-2" data-id="element-504">
              Do not show features. Show the reveal.
            </span>
          </p>
        </div>
        <div data-id="element-505">
          <p className="text-sm uppercase tracking-wider text-white/70 font-bold mb-2" data-id="element-506">
            Why it works
          </p>
          <p className="text-white/90 leading-relaxed" data-id="element-507">
            TikTok and Reels are dopamine delivery systems. The reveal gives
            viewers a reason to watch to the end, and a reason to comment{' '}
            <em className="text-white" data-id="element-508">("omg do mine," "what is this app")</em>.
            Both behaviors drive algorithmic distribution.
          </p>
        </div>
      </section>

      {/* Four formats intro */}
      <section data-id="element-509">
        <h3 className="text-2xl md:text-3xl font-black text-warmBrown mb-2" data-id="element-510">
          The Four Organic Formats
        </h3>
        <p className="text-warmBrown/70 leading-relaxed mb-8" data-id="element-511">
          Ranked by priority. Mix and match these for your weekly content
          schedule.
        </p>

        <div className="space-y-5" data-id="element-512">
          {formats.map(format => {
          const c = colorMap[format.color];
          return <article key={format.number} className="bg-white rounded-3xl p-8 shadow-soft border border-warmBrown/5" data-id="element-513">
                <div className="flex items-start gap-4 mb-5" data-id="element-514">
                  <div className={`w-14 h-14 rounded-2xl ${c.solid} text-white flex items-center justify-center flex-shrink-0 font-black text-xl`} data-id="element-515">
                    {format.number}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-516">
                    <p className={`text-xs font-bold uppercase tracking-wider ${c.text} mb-1`} data-id="element-517">
                      Format {format.number} · {format.priority}
                    </p>
                    <h4 className="text-xl md:text-2xl font-black text-warmBrown" data-id="element-518">
                      {format.title}
                    </h4>
                  </div>
                </div>

                <div className="mb-5" data-id="element-519">
                  <p className="text-xs font-bold uppercase tracking-wider text-warmBrown/40 mb-2" data-id="element-520">
                    Structure
                  </p>
                  <p className="text-warmBrown/80 leading-relaxed" data-id="element-521">
                    {format.structure}
                  </p>
                </div>

                {format.hooks.length > 0 && <div className="mb-5" data-id="element-522">
                    <p className="text-xs font-bold uppercase tracking-wider text-warmBrown/40 mb-3" data-id="element-523">
                      Hook options
                    </p>
                    <div className="space-y-2" data-id="element-524">
                      {format.hooks.map(hook => <div key={hook} className={`bg-cream/60 rounded-xl px-4 py-3 text-warmBrown/80 italic border-l-4 ${c.text.replace('text-', 'border-')}/40`} data-id="element-525">
                          {hook}
                        </div>)}
                    </div>
                  </div>}

                <div className={`${c.bg} rounded-2xl p-5`} data-id="element-526">
                  <div className="flex items-center gap-2 mb-2" data-id="element-527">
                    <Lightbulb size={16} className={c.text} data-id="element-528" />
                    <p className={`font-bold text-sm ${c.text}`} data-id="element-529">
                      Why it works
                    </p>
                  </div>
                  <p className="text-warmBrown/80 text-sm leading-relaxed" data-id="element-530">
                    {format.why}
                  </p>
                </div>
              </article>;
        })}
        </div>
      </section>
    </motion.article>;
}
function HooksTopic() {
  const categories = [{
    label: 'Emotional / Attachment',
    icon: <HeartHandshake size={22} data-id="element-531" />,
    color: 'coral' as const,
    hooks: ['"I cried when I saw my cat as an animated character [she has her exact markings]"', '"This app turned my dead dog into a living animated version of him [I\'m not okay]"', '"POV: your actual pet becomes your virtual self-care companion"']
  }, {
    label: 'Accountability / Consequence',
    icon: <AlertCircle size={22} data-id="element-532" />,
    color: 'peach' as const,
    hooks: ['"My virtual pet literally got sick because I didn\'t sleep enough [I deserved it]"', '"The only thing that made me actually do my morning routine is not wanting to let my cat down"', '"What happens to your Pawchi when you skip days [warning: sad]"']
  }, {
    label: 'Curiosity / Transformation',
    icon: <HelpCircle size={22} data-id="element-533" />,
    color: 'lavender' as const,
    hooks: ['"Watch me turn my dog into a Tamagotchi [AI did this in 10 seconds]"', '"I uploaded my cat\'s photo and now I have to take care of her digitally"', '"This app is literally Tamagotchi but your actual pet [how is this free]"']
  }, {
    label: 'Nostalgia',
    icon: <Gamepad2 size={22} data-id="element-534" />,
    color: 'sage' as const,
    hooks: ['"Tamagotchi but it\'s YOUR actual pet and it\'s connected to your real habits"', '"Grew up with Tamagotchis, now I have one but it looks exactly like my dog"', '"POV: it\'s 2006 and your Tamagotchi is your actual cat"']
  }, {
    label: 'Identity',
    icon: <Fingerprint size={22} data-id="element-535" />,
    color: 'peach' as const,
    hooks: ['"Self-care apps never worked for me until my cat\'s wellbeing depended on it"', '"I\'m a terrible habit person but I\'ve been consistent for 30 days because of this app"', '"Turns out the only accountability system I needed was a virtual version of my own pet"']
  }];
  const colorMap = {
    peach: {
      bg: 'bg-peach/10',
      text: 'text-peach',
      border: 'border-peach/40'
    },
    coral: {
      bg: 'bg-coral/10',
      text: 'text-coral',
      border: 'border-coral/40'
    },
    lavender: {
      bg: 'bg-lavender/10',
      text: 'text-lavender',
      border: 'border-lavender/40'
    },
    sage: {
      bg: 'bg-sage/10',
      text: 'text-sage',
      border: 'border-sage/40'
    }
  };
  const commentBait = ['Flash a leaderboard showing a rival pet beating the user\'s pet (provokes "how do you compete?" comments)', 'Show a strange/unexpected pet name or stat combination', 'Show the pet\'s "disapproval" animation', 'Anything that makes viewers feel they saw something and need to comment about it'];
  return <motion.article initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4
  }} className="space-y-10" data-id="element-536">
      {/* Intro */}
      <header data-id="element-537">
        <div className="inline-flex items-center gap-2 bg-peach/10 text-peach px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4" data-id="element-538">
          <Anchor size={14} data-id="element-539" />
          Hook Library
        </div>
        <h2 className="text-3xl md:text-4xl font-black text-warmBrown mb-4" data-id="element-540">
          Hooks &amp; Viral Angles
        </h2>
        <p className="text-warmBrown/80 text-lg leading-relaxed" data-id="element-541">
          The first 3 seconds decide everything. Swipe these{' '}
          <strong className="text-warmBrown" data-id="element-542">
            highest-leverage hook angles
          </strong>{' '}
          for Pawchi, then customize them with details about your specific pet.
        </p>
      </header>

      {/* Hook categories */}
      <section className="space-y-5" data-id="element-543">
        {categories.map(cat => {
        const c = colorMap[cat.color];
        return <article key={cat.label} className="bg-white rounded-3xl p-8 shadow-soft border border-warmBrown/5" data-id="element-544">
              <div className="flex items-center gap-4 mb-5" data-id="element-545">
                <div className={`w-12 h-12 rounded-full ${c.bg} ${c.text} flex items-center justify-center flex-shrink-0`} data-id="element-546">
                  {cat.icon}
                </div>
                <h3 className="text-xl md:text-2xl font-black text-warmBrown" data-id="element-547">
                  {cat.label} Hooks
                </h3>
              </div>

              <div className="space-y-2" data-id="element-548">
                {cat.hooks.map(hook => <div key={hook} className={`bg-cream/60 rounded-xl px-4 py-3 text-warmBrown/80 italic border-l-4 ${c.border}`} data-id="element-549">
                    {hook}
                  </div>)}
              </div>
            </article>;
      })}
      </section>

      {/* Hook structure formula */}
      <section className="bg-gradient-to-br from-peach to-coral rounded-3xl p-8 md:p-10 text-white" data-id="element-550">
        <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4" data-id="element-551">
          <Sparkles size={14} data-id="element-552" />
          The Formula
        </div>
        <h3 className="text-3xl font-black mb-4" data-id="element-553">Hook Structure Formula</h3>
        <p className="text-white/90 leading-relaxed mb-6" data-id="element-554">
          For Pawchi specifically, the highest-performing hook pattern is:
        </p>

        <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-8" data-id="element-555">
          <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-xl font-bold text-sm md:text-base border border-white/30" data-id="element-556">
            Specific personal detail
          </span>
          <Plus size={18} className="text-white/60" data-id="element-557" />
          <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-xl font-bold text-sm md:text-base border border-white/30" data-id="element-558">
            Surprising outcome
          </span>
          <Plus size={18} className="text-white/60" data-id="element-559" />
          <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-xl font-bold text-sm md:text-base border border-white/30" data-id="element-560">
            Implicit challenge / FOMO
          </span>
        </div>

        <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-6 border border-white/20" data-id="element-561">
          <p className="text-sm uppercase tracking-wider text-white/70 font-bold mb-3" data-id="element-562">
            Example
          </p>
          <p className="text-white/95 leading-relaxed italic" data-id="element-563">
            "I uploaded my{' '}
            <strong className="text-white not-italic bg-white/20 px-1.5 py-0.5 rounded" data-id="element-564">
              grey tabby's photo
            </strong>{' '}
            [specific] and she now has{' '}
            <strong className="text-white not-italic bg-white/20 px-1.5 py-0.5 rounded" data-id="element-565">
              her exact markings in the app
            </strong>{' '}
            [surprising] and{' '}
            <strong className="text-white not-italic bg-white/20 px-1.5 py-0.5 rounded" data-id="element-566">
              my water intake has never been higher
            </strong>{' '}
            [FOMO/outcome]"
          </p>
        </div>
      </section>

      {/* Comment bait */}
      <section className="bg-white rounded-3xl p-8 md:p-10 shadow-soft border border-warmBrown/5" data-id="element-567">
        <div className="flex items-start gap-4 mb-5" data-id="element-568">
          <div className="w-12 h-12 rounded-full bg-lavender/10 text-lavender flex items-center justify-center flex-shrink-0" data-id="element-569">
            <MessageSquare size={22} data-id="element-570" />
          </div>
          <div data-id="element-571">
            <h3 className="text-2xl font-black text-warmBrown mb-1" data-id="element-572">
              Comment Bait Elements
            </h3>
            <p className="text-warmBrown/60 text-sm" data-id="element-573">
              Hide something that provokes a reaction.
            </p>
          </div>
        </div>

        <p className="text-warmBrown/80 leading-relaxed mb-6" data-id="element-574">
          Hide something in the video that provokes comments. It{' '}
          <strong className="text-warmBrown" data-id="element-575">
            does not need to be related to the app
          </strong>
          :
        </p>

        <ul className="space-y-3 mb-6" data-id="element-576">
          {commentBait.map(item => <li key={item} className="flex items-start gap-3" data-id="element-577">
              <span className="mt-2 w-2 h-2 rounded-full bg-lavender flex-shrink-0" data-id="element-578"></span>
              <span className="text-warmBrown/80 leading-relaxed" data-id="element-579">{item}</span>
            </li>)}
        </ul>

        <div className="bg-warmBrown rounded-2xl p-5 text-cream" data-id="element-580">
          <p className="font-bold text-center text-lg" data-id="element-581">
            Comments → engagement → cheaper CPM on ads → more distribution.
          </p>
        </div>
      </section>
    </motion.article>;
}
function FinchPawchiTopic() {
  const pairs = [{
    finch: 'Nobody: That one person who remembers to drink water because a bird on her phone told her to',
    pawchi: 'Nobody: That one person drinking water because a pixel version of her dog is judging her',
    referenceUrl: 'https://www.tiktok.com/@finchcare/video/7618231189844806926'
  }, {
    finch: 'Me trying to drink water so I can get more rainbow stones on Finch',
    pawchi: 'Me chugging water because I turned my dog into a Tamagotchi',
    referenceUrl: 'https://www.tiktok.com/@finchcare/video/7644182635153001742'
  }, {
    finch: 'Making my stupid bed so my Finch can get back from her stupid adventure',
    pawchi: 'Making my stupid bed because my dog somehow lives in my phone now',
    referenceUrl: 'https://www.tiktok.com/@madiebooth/video/7474039283083529503'
  }, {
    finch: 'Rotting in bed? Not anymore thanks to this cute self care app',
    pawchi: "Rotting in bed? My dog won't let me anymore",
    referenceUrl: 'https://www.tiktok.com/@finchcare/video/7457035886274907438'
  }, {
    finch: 'POV: your sink is overflowing with dishes and the only motivation to wash them is because of a silly little bird on your phone',
    pawchi: "POV: the only reason these dishes are getting done is because my dog gets sad when I don't",
    referenceUrl: 'https://www.tiktok.com/@finchcare/video/7642362206276881678'
  }, {
    finch: 'Me trying to act nonchalant but I literally have a bird on my phone reminding me to drink water',
    pawchi: 'Me trying to explain why a digital version of my dog controls my life',
    referenceUrl: 'https://www.tiktok.com/@finchcare/video/7644182635153001742'
  }, {
    finch: 'Download a silly little self care app not expecting it to work',
    pawchi: 'Downloaded an app that turned my dog into a virtual pet. Somehow my life is together now',
    referenceUrl: 'https://www.tiktok.com/@finchcare/video/7263861893255761194'
  }, {
    finch: 'I love being a girl in 2026 because someone made a Studio Ghibli self care app',
    pawchi: 'I love being alive in 2026 because I can turn my dog into a Tamagotchi',
    referenceUrl: 'https://www.tiktok.com/@reb.ponyo/video/7627477323406642445'
  }, {
    finch: 'POV: this silly goofy bird on your phone is the reason why you remember to treat yourself',
    pawchi: "POV: my dog has absolutely no idea he's responsible for my mental health now",
    referenceUrl: 'https://www.tiktok.com/@finchcare/video/7619002656144575758'
  }, {
    finch: "Checks my birb to see if she's done with her adventure",
    pawchi: 'Checking if my dog survived the day after I forgot to drink water',
    referenceUrl: 'https://www.tiktok.com/@finchcare/video/7129210768452521259'
  }];
  const bonusReferences = [{
    caption: 'Me, stressy, depressy and unmotivated',
    url: 'https://www.tiktok.com/@finchcare/video/7068061545644707118',
    handle: '@finchcare'
  }, {
    caption: 'Me: last year, "I wish I was healthier"',
    url: 'https://www.tiktok.com/@tylerandgerald/video/7314567467391700270',
    handle: '@tylerandgerald'
  }, {
    caption: 'Making my stupid bed',
    url: 'https://www.tiktok.com/@finchcare/video/7213858957826329899',
    handle: '@finchcare'
  }];
  function getHandle(url: string) {
    const match = url.match(/@([^/]+)/);
    return match ? `@${match[1]}` : '';
  }
  return <motion.article initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4
  }} className="space-y-10" data-id="element-582">
      {/* Intro */}
      <header data-id="element-583">
        <div className="inline-flex items-center gap-2 bg-lavender/10 text-lavender px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4" data-id="element-584">
          <Copy size={14} data-id="element-585" />
          Swipe File
        </div>
        <h2 className="text-3xl md:text-4xl font-black text-warmBrown mb-4" data-id="element-586">
          Finch → Pawchi Examples
        </h2>
        <p className="text-warmBrown/80 text-lg leading-relaxed" data-id="element-587">
          Finch's UGC playbook is already proven. We've remixed{' '}
          <strong className="text-warmBrown" data-id="element-588">
            {pairs.length} of their top-performing caption styles
          </strong>{' '}
          for Pawchi. Use them as templates — swap in your pet's details and
          post.
        </p>
      </header>

      {/* Legend */}
      <div className="grid sm:grid-cols-2 gap-3" data-id="element-589">
        <div className="bg-white rounded-2xl p-4 border border-warmBrown/10 flex items-center gap-3" data-id="element-590">
          <div className="w-10 h-10 rounded-full bg-warmBrown/10 text-warmBrown/60 flex items-center justify-center flex-shrink-0" data-id="element-591">
            <Bird size={20} data-id="element-592" />
          </div>
          <div data-id="element-593">
            <p className="text-xs font-bold uppercase tracking-wider text-warmBrown/40" data-id="element-594">
              Original
            </p>
            <p className="font-bold text-warmBrown text-sm" data-id="element-595">Finch caption</p>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-peach/30 flex items-center gap-3" data-id="element-596">
          <div className="w-10 h-10 rounded-full bg-peach/10 text-peach flex items-center justify-center flex-shrink-0" data-id="element-597">
            <PawPrint size={20} data-id="element-598" />
          </div>
          <div data-id="element-599">
            <p className="text-xs font-bold uppercase tracking-wider text-peach" data-id="element-600">
              Remixed
            </p>
            <p className="font-bold text-warmBrown text-sm" data-id="element-601">Pawchi variant</p>
          </div>
        </div>
      </div>

      {/* Comparison pairs */}
      <section className="space-y-5" data-id="element-602">
        {pairs.map((pair, index) => <article key={index} className="bg-white rounded-3xl p-6 md:p-8 shadow-soft border border-warmBrown/5" data-id="element-603">
            <div className="flex items-center gap-3 mb-5" data-id="element-604">
              <span className="w-8 h-8 rounded-full bg-warmBrown text-cream flex items-center justify-center font-black text-sm" data-id="element-605">
                {index + 1}
              </span>
              <span className="text-xs font-bold uppercase tracking-wider text-warmBrown/40" data-id="element-606">
                Caption Pair
              </span>
            </div>

            <div className="grid md:grid-cols-[1fr_auto_1fr] gap-4 md:gap-5 items-stretch" data-id="element-607">
              {/* Finch side */}
              <div className="bg-cream/60 rounded-2xl p-5 border border-warmBrown/10" data-id="element-608">
                <div className="flex items-center gap-2 mb-3" data-id="element-609">
                  <Bird size={16} className="text-warmBrown/40" data-id="element-610" />
                  <p className="text-xs font-bold uppercase tracking-wider text-warmBrown/40" data-id="element-611">
                    Finch
                  </p>
                </div>
                <p className="text-warmBrown/80 italic leading-relaxed" data-id="element-612">
                  "{pair.finch}"
                </p>
              </div>

              {/* Arrow */}
              <div className="flex md:flex-col items-center justify-center text-peach md:py-4" data-id="element-613">
                <ArrowRight size={24} className="md:hidden" data-id="element-614" />
                <ArrowRight size={24} className="hidden md:block" data-id="element-615" />
              </div>

              {/* Pawchi side */}
              <div className="bg-peach/10 rounded-2xl p-5 border border-peach/30" data-id="element-616">
                <div className="flex items-center justify-between gap-2 mb-3" data-id="element-617">
                  <div className="flex items-center gap-2" data-id="element-618">
                    <PawPrint size={16} className="text-peach" data-id="element-619" />
                    <p className="text-xs font-bold uppercase tracking-wider text-peach" data-id="element-620">
                      Pawchi
                    </p>
                  </div>
                  {pair.referenceUrl && <a href={pair.referenceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-peach hover:text-coral transition-colors" data-id="element-621">
                      <Film size={12} data-id="element-622" />
                      <span data-id="element-623">{getHandle(pair.referenceUrl)}</span>
                    </a>}
                </div>
                <p className="text-warmBrown leading-relaxed font-medium" data-id="element-624">
                  "{pair.pawchi}"
                </p>
              </div>
            </div>
          </article>)}
      </section>

      {/* Pawchi-original captions */}
      <section className="bg-white rounded-3xl p-8 md:p-10 shadow-soft border border-warmBrown/5" data-id="element-625">
        <div className="flex items-start gap-4 mb-6" data-id="element-626">
          <div className="w-12 h-12 rounded-full bg-peach/10 text-peach flex items-center justify-center flex-shrink-0" data-id="element-627">
            <PawPrint size={22} data-id="element-628" />
          </div>
          <div data-id="element-629">
            <h3 className="text-2xl font-black text-warmBrown mb-1" data-id="element-630">
              More Pawchi-Original Captions
            </h3>
            <p className="text-warmBrown/60 text-sm" data-id="element-631">
              Original ideas built around the chase-and-transform moment — no
              Finch counterpart.
            </p>
          </div>
        </div>

        <div className="space-y-3" data-id="element-632">
          {["POV: me chasing my dog around the house because apparently turning him into a Tamagotchi is the only way I'll start taking care of myself.", "I turned my dog into a Tamagotchi and now he's somehow managing my life better than I am.", 'Was trying to make a digital version of my dog. Accidentally ended up in a healthier lifestyle.', "Me: can't stick to a habit tracker for more than 3 days.\nAlso me: chasing my dog so I can turn him into a Tamagotchi and finally get my life together."].map((caption, idx) => <div key={idx} className="bg-peach/10 rounded-2xl p-5 border border-peach/30" data-id="element-633">
              <div className="flex items-center gap-2 mb-2" data-id="element-634">
                <span className="w-6 h-6 rounded-full bg-peach text-white flex items-center justify-center font-black text-xs flex-shrink-0" data-id="element-635">
                  {idx + 1}
                </span>
                <p className="text-xs font-bold uppercase tracking-wider text-peach" data-id="element-636">
                  Pawchi
                </p>
              </div>
              <p className="text-warmBrown leading-relaxed font-medium whitespace-pre-line" data-id="element-637">
                "{caption}"
              </p>
            </div>)}
        </div>
      </section>

      {/* Bonus references */}
      <section className="bg-white rounded-3xl p-8 md:p-10 shadow-soft border border-warmBrown/5" data-id="element-638">
        <div className="flex items-start gap-4 mb-6" data-id="element-639">
          <div className="w-12 h-12 rounded-full bg-lavender/10 text-lavender flex items-center justify-center flex-shrink-0" data-id="element-640">
            <Film size={22} data-id="element-641" />
          </div>
          <div data-id="element-642">
            <h3 className="text-2xl font-black text-warmBrown mb-1" data-id="element-643">
              Bonus Reference Videos
            </h3>
            <p className="text-warmBrown/60 text-sm" data-id="element-644">
              Extra TikToks in the Finch / self-care UGC style worth studying.
            </p>
          </div>
        </div>

        <div className="space-y-3" data-id="element-645">
          {bonusReferences.map((ref, idx) => <a key={idx} href={ref.url} target="_blank" rel="noopener noreferrer" className="group flex items-center justify-between gap-4 p-4 bg-cream/60 hover:bg-peach/10 rounded-xl border border-warmBrown/5 hover:border-peach/30 transition-all" data-id="element-646">
              <div className="flex items-center gap-3 min-w-0" data-id="element-647">
                <div className="w-9 h-9 rounded-lg bg-white text-peach flex items-center justify-center flex-shrink-0 shadow-soft" data-id="element-648">
                  <Film size={16} data-id="element-649" />
                </div>
                <div className="min-w-0" data-id="element-650">
                  <p className="font-medium text-warmBrown italic truncate" data-id="element-651">
                    "{ref.caption}"
                  </p>
                  <p className="text-xs text-warmBrown/50 font-bold" data-id="element-652">
                    {ref.handle}
                  </p>
                </div>
              </div>
              <ArrowRight size={18} className="text-warmBrown/30 group-hover:text-peach group-hover:translate-x-1 transition-all flex-shrink-0" data-id="element-653" />
            </a>)}
        </div>
      </section>

      {/* Closing tip */}
      <Callout color="lavender" icon={<Lightbulb size={18} data-id="element-655" />} title="How to use this swipe file" data-id="element-654">
        Don't copy-paste verbatim. Pick a caption that fits your video, then{' '}
        <strong data-id="element-656">swap in specific details about your pet</strong> — breed, name,
        a unique quirk. The more specific, the higher the engagement.
      </Callout>
    </motion.article>;
}
function UgcBriefTopic() {
  const briefComponents = [{
    icon: <Anchor size={18} data-id="element-657" />,
    label: 'The hook',
    desc: 'Exact words and delivery style to open with'
  }, {
    icon: <Wand2 size={18} data-id="element-658" />,
    label: 'The transformation demo',
    desc: 'Exactly how to show pet upload → animated result'
  }, {
    icon: <Heart size={18} data-id="element-659" />,
    label: 'The self-care loop',
    desc: 'How to connect habits to pet health in the demo'
  }, {
    icon: <Smile size={18} data-id="element-660" />,
    label: 'The emotional beat',
    desc: 'The moment of genuine delight or surprise to capture'
  }, {
    icon: <Link2 size={18} data-id="element-661" />,
    label: 'The CTA',
    desc: '"Comment your pet and I\'ll show you" / link in bio / App Store'
  }];
  const hooks = ['"I accidentally found the cutest way to stay consistent with my habits."', '"My dog is now judging my self-care."', '"This app turned my pet into a little Tamagotchi."', '"I uploaded a photo of my pet and wasn\'t expecting this."'];
  const transformationSteps = ['Show the real pet first.', 'Capture a cute moment of the pet looking at the camera.', 'Open Pawchi and upload a photo of the pet.', 'Record the transformation process.', 'Reveal the generated Pawchi companion.', 'Spend a few seconds showing the resemblance between the real pet and the digital version.', 'Encourage creators to react genuinely to the result.'];
  const selfCareSteps = ['Briefly explain or demonstrate that completing habits affects the pet.', 'Show one or two habit completions such as drinking water, taking a walk, journaling, or checking in.', 'Return to the app and show the pet reacting positively.', 'The focus should be on the emotional connection, not on explaining features.'];
  const ctas = ['"If you have a pet, you need to try this."', '"Upload your pet and see what it becomes."', '"Link in bio if you want your own Pawchi."', '"Comment your pet and I\'ll show you what it would look like."'];
  const donts = ['Do not mention pricing.', 'Do not mention subscriptions.', 'Do not show paywalls.', 'Do not say "download now".', 'Do not make it sound like a traditional ad.', 'Do not over-explain features.', 'Do not use corporate language.'];
  return <motion.article initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4
  }} className="space-y-10" data-id="element-662">
      {/* Intro */}
      <header data-id="element-663">
        <div className="inline-flex items-center gap-2 bg-sage/10 text-sage px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4" data-id="element-664">
          <FileText size={14} data-id="element-665" />
          Creative Brief
        </div>
        <h2 className="text-3xl md:text-4xl font-black text-warmBrown mb-4" data-id="element-666">
          UGC Brief Template
        </h2>
        <p className="text-warmBrown/80 text-lg leading-relaxed" data-id="element-667">
          The full creative brief for Pawchi videos. Use this as your shot list
          — every video should hit{' '}
          <strong className="text-warmBrown" data-id="element-668">all five components</strong> and
          avoid the don'ts.
        </p>
      </header>

      {/* Goal */}
      <section className="bg-gradient-to-br from-peach to-coral rounded-3xl p-8 md:p-10 text-white" data-id="element-669">
        <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4" data-id="element-670">
          <Target size={14} data-id="element-671" />
          The Goal
        </div>
        <p className="text-xl md:text-2xl font-bold leading-snug" data-id="element-672">
          Show how a real pet can be turned into a digital companion that
          motivates self-care.
        </p>
        <p className="text-white/90 leading-relaxed mt-4" data-id="element-673">
          The video should feel like a{' '}
          <strong className="text-white" data-id="element-674">personal discovery</strong>, not a
          sponsored promotion.
        </p>
      </section>

      {/* Brief components overview */}
      <section className="bg-white rounded-3xl p-8 md:p-10 shadow-soft border border-warmBrown/5" data-id="element-675">
        <h3 className="text-2xl font-black text-warmBrown mb-2" data-id="element-676">
          Every brief includes 5 components
        </h3>
        <p className="text-warmBrown/60 text-sm mb-6" data-id="element-677">
          Hit all five — in this order — for a complete video.
        </p>

        <div className="space-y-3" data-id="element-678">
          {briefComponents.map((item, index) => <div key={item.label} className="flex items-center gap-4 p-4 bg-cream/60 rounded-xl" data-id="element-679">
              <span className="w-8 h-8 rounded-full bg-peach text-white flex items-center justify-center font-black text-sm flex-shrink-0" data-id="element-680">
                {index + 1}
              </span>
              <div className="text-peach flex-shrink-0" data-id="element-681">{item.icon}</div>
              <div className="flex-1 min-w-0" data-id="element-682">
                <p className="font-bold text-warmBrown" data-id="element-683">{item.label}</p>
                <p className="text-sm text-warmBrown/60" data-id="element-684">{item.desc}</p>
              </div>
            </div>)}
        </div>
      </section>

      {/* Step 1: Hook */}
      <BriefStep number={1} title="Hook" subtitle="Open within the first 1–2 seconds." color="peach" icon={<Anchor size={22} data-id="element-686" />} data-id="element-685">
        <p className="text-xs font-bold uppercase tracking-wider text-warmBrown/40 mb-3" data-id="element-687">
          Choose one
        </p>
        <div className="space-y-2 mb-5" data-id="element-688">
          {hooks.map(hook => <div key={hook} className="bg-cream/60 rounded-xl px-4 py-3 text-warmBrown/80 italic border-l-4 border-peach/40" data-id="element-689">
              {hook}
            </div>)}
        </div>
        <Callout color="peach" icon={<Megaphone size={16} data-id="element-691" />} title="Delivery" data-id="element-690">
          Should feel natural, slightly surprised, and conversational.
        </Callout>
      </BriefStep>

      {/* Step 2: Transformation Demo */}
      <BriefStep number={2} title="Transformation Demo" subtitle="The reveal is the hero of the video." color="coral" icon={<Film size={22} data-id="element-693" />} data-id="element-692">
        <ol className="space-y-3" data-id="element-694">
          {transformationSteps.map((step, idx) => <li key={step} className="flex items-start gap-3" data-id="element-695">
              <span className="mt-0.5 w-6 h-6 rounded-full bg-coral/15 text-coral flex items-center justify-center font-black text-xs flex-shrink-0" data-id="element-696">
                {idx + 1}
              </span>
              <span className="text-warmBrown/80 leading-relaxed" data-id="element-697">{step}</span>
            </li>)}
        </ol>
      </BriefStep>

      {/* Step 3: Self-Care Loop */}
      <BriefStep number={3} title="Self-Care Loop" subtitle="Connect habits to pet wellbeing." color="lavender" icon={<Heart size={22} data-id="element-699" />} data-id="element-698">
        <ol className="space-y-3 mb-5" data-id="element-700">
          {selfCareSteps.map((step, idx) => <li key={step} className="flex items-start gap-3" data-id="element-701">
              <span className="mt-0.5 w-6 h-6 rounded-full bg-lavender/15 text-lavender flex items-center justify-center font-black text-xs flex-shrink-0" data-id="element-702">
                {idx + 1}
              </span>
              <span className="text-warmBrown/80 leading-relaxed" data-id="element-703">{step}</span>
            </li>)}
        </ol>
      </BriefStep>

      {/* Step 4: Emotional Beat */}
      <BriefStep number={4} title="Emotional Beat" subtitle="The single most important moment in the video." color="sage" icon={<Smile size={22} data-id="element-705" />} data-id="element-704">
        <p className="text-warmBrown/80 leading-relaxed mb-4" data-id="element-706">
          Capture the moment where the creator realizes:
        </p>
        <div className="bg-sage/10 border-l-4 border-sage/50 rounded-xl px-5 py-4 text-warmBrown/90 italic leading-relaxed mb-5" data-id="element-707">
          "Wait… now I actually want to complete my habits because I don't want
          my pet to be sad."
        </div>
        <Callout color="sage" icon={<Sparkles size={16} data-id="element-709" />} title="What to capture" data-id="element-708">
          The creator should <strong data-id="element-710">smile, laugh, or react naturally</strong>{' '}
          to the pet's response. This is the moment that converts viewers.
        </Callout>
      </BriefStep>

      {/* Step 5: CTA */}
      <BriefStep number={5} title="CTA" subtitle="End on an invitation, not a sales pitch." color="peach" icon={<Link2 size={22} data-id="element-712" />} data-id="element-711">
        <p className="text-xs font-bold uppercase tracking-wider text-warmBrown/40 mb-3" data-id="element-713">
          Choose one
        </p>
        <div className="space-y-2" data-id="element-714">
          {ctas.map(cta => <div key={cta} className="bg-cream/60 rounded-xl px-4 py-3 text-warmBrown/80 italic border-l-4 border-peach/40" data-id="element-715">
              {cta}
            </div>)}
        </div>
      </BriefStep>

      {/* DO NOT section */}
      <section className="bg-warmBrown rounded-3xl p-8 md:p-10 text-cream" data-id="element-716">
        <div className="flex items-start gap-4 mb-6" data-id="element-717">
          <div className="w-12 h-12 rounded-full bg-coral/20 text-coral flex items-center justify-center flex-shrink-0" data-id="element-718">
            <Ban size={22} data-id="element-719" />
          </div>
          <div data-id="element-720">
            <h3 className="text-2xl font-black mb-1" data-id="element-721">DO NOT</h3>
            <p className="text-cream/60 text-sm" data-id="element-722">
              These will sink the video. Avoid every one.
            </p>
          </div>
        </div>

        <ul className="space-y-3" data-id="element-723">
          {donts.map(item => <li key={item} className="flex items-start gap-3" data-id="element-724">
              <XCircle size={18} className="text-coral mt-0.5 flex-shrink-0" data-id="element-725" />
              <span className="text-cream/85 leading-relaxed" data-id="element-726">{item}</span>
            </li>)}
        </ul>
      </section>

      {/* Closing mantra */}
      <section className="bg-white rounded-3xl p-8 md:p-10 shadow-soft border border-warmBrown/5 text-center" data-id="element-727">
        <p className="text-xs font-bold uppercase tracking-wider text-warmBrown/40 mb-4" data-id="element-728">
          Think
        </p>
        <p className="text-2xl md:text-3xl font-black text-warmBrown leading-tight" data-id="element-729">
          Personal discovery. Emotional reaction. Cute pet moment. Self-care
          motivation.
        </p>
      </section>
    </motion.article>;
}
interface BriefStepProps {
  number: number;
  title: string;
  subtitle: string;
  color: 'peach' | 'coral' | 'lavender' | 'sage';
  icon: React.ReactNode;
  children: React.ReactNode;
}
function BriefStep({
  number,
  title,
  subtitle,
  color,
  icon,
  children
}: BriefStepProps) {
  const colorMap = {
    peach: {
      bg: 'bg-peach/10',
      text: 'text-peach',
      solid: 'bg-peach'
    },
    coral: {
      bg: 'bg-coral/10',
      text: 'text-coral',
      solid: 'bg-coral'
    },
    lavender: {
      bg: 'bg-lavender/10',
      text: 'text-lavender',
      solid: 'bg-lavender'
    },
    sage: {
      bg: 'bg-sage/10',
      text: 'text-sage',
      solid: 'bg-sage'
    }
  };
  const c = colorMap[color];
  return <section className="bg-white rounded-3xl p-8 md:p-10 shadow-soft border border-warmBrown/5" data-id="element-730">
      <div className="flex items-start gap-4 mb-6" data-id="element-731">
        <div className={`w-14 h-14 rounded-2xl ${c.solid} text-white flex items-center justify-center flex-shrink-0 font-black text-xl`} data-id="element-732">
          {number}
        </div>
        <div className="flex-1 min-w-0" data-id="element-733">
          <div className="flex items-center gap-2 mb-1" data-id="element-734">
            <span className={c.text} data-id="element-735">{icon}</span>
            <p className={`text-xs font-bold uppercase tracking-wider ${c.text}`} data-id="element-736">
              Step {number}
            </p>
          </div>
          <h3 className="text-2xl font-black text-warmBrown mb-1" data-id="element-737">{title}</h3>
          <p className="text-warmBrown/60 text-sm" data-id="element-738">{subtitle}</p>
        </div>
      </div>
      <div data-id="element-739">{children}</div>
    </section>;
}
interface EngagementBucketProps {
  percent: number;
  label: string;
  color: 'peach' | 'coral' | 'lavender' | 'sage';
  items: string[];
  goal: string;
}
function EngagementBucket({
  percent,
  label,
  color,
  items,
  goal
}: EngagementBucketProps) {
  const colorMap = {
    peach: {
      bg: 'bg-peach/10',
      text: 'text-peach',
      solid: 'bg-peach',
      dot: 'bg-peach'
    },
    coral: {
      bg: 'bg-coral/10',
      text: 'text-coral',
      solid: 'bg-coral',
      dot: 'bg-coral'
    },
    lavender: {
      bg: 'bg-lavender/10',
      text: 'text-lavender',
      solid: 'bg-lavender',
      dot: 'bg-lavender'
    },
    sage: {
      bg: 'bg-sage/10',
      text: 'text-sage',
      solid: 'bg-sage',
      dot: 'bg-sage'
    }
  };
  const c = colorMap[color];
  return <div className="bg-cream/60 rounded-2xl p-5 md:p-6" data-id="element-740">
      <div className="flex items-center gap-4 mb-4" data-id="element-741">
        <div className={`${c.solid} text-white font-black text-lg md:text-xl rounded-2xl px-4 py-2 flex-shrink-0`} data-id="element-742">
          {percent}%
        </div>
        <h4 className="font-black text-warmBrown text-lg md:text-xl" data-id="element-743">
          {label}
        </h4>
      </div>

      {/* Visual percentage bar */}
      <div className="h-1.5 bg-warmBrown/5 rounded-full overflow-hidden mb-5" data-id="element-744">
        <div className={`h-full ${c.solid} rounded-full`} style={{
        width: `${percent}%`
      }} data-id="element-745" />
      </div>

      <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-2 mb-5" data-id="element-746">
        {items.map(item => <li key={item} className="flex items-center gap-2 text-warmBrown/80 text-sm" data-id="element-747">
            <span className={`w-1.5 h-1.5 rounded-full ${c.dot} flex-shrink-0`} data-id="element-748"></span>
            {item}
          </li>)}
      </ul>

      <div className={`${c.bg} rounded-xl px-4 py-3`} data-id="element-749">
        <p className="text-xs font-bold uppercase tracking-wider text-warmBrown/40 mb-1" data-id="element-750">
          Goal
        </p>
        <p className={`text-sm font-bold ${c.text}`} data-id="element-751">{goal}</p>
      </div>
    </div>;
}
interface PhaseCardProps {
  phase: string;
  title: string;
  color: 'peach' | 'coral' | 'lavender' | 'sage';
  icon: React.ReactNode;
  children: React.ReactNode;
}
function PhaseCard({
  phase,
  title,
  color,
  icon,
  children
}: PhaseCardProps) {
  const colorMap = {
    peach: {
      bg: 'bg-peach/10',
      text: 'text-peach'
    },
    coral: {
      bg: 'bg-coral/10',
      text: 'text-coral'
    },
    lavender: {
      bg: 'bg-lavender/10',
      text: 'text-lavender'
    },
    sage: {
      bg: 'bg-sage/10',
      text: 'text-sage'
    }
  };
  const c = colorMap[color];
  return <section className="bg-white rounded-3xl p-8 md:p-10 shadow-soft border border-warmBrown/5" data-id="element-752">
      <div className="flex items-start gap-4 mb-6" data-id="element-753">
        <div className={`w-12 h-12 rounded-full ${c.bg} ${c.text} flex items-center justify-center flex-shrink-0`} data-id="element-754">
          {icon}
        </div>
        <div data-id="element-755">
          <p className={`text-xs font-bold uppercase tracking-wider ${c.text} mb-1`} data-id="element-756">
            {phase}
          </p>
          <h3 className="text-2xl font-black text-warmBrown" data-id="element-757">{title}</h3>
        </div>
      </div>
      <div className="space-y-4" data-id="element-758">{children}</div>
    </section>;
}
interface RuleProps {
  icon: React.ReactNode;
  children: React.ReactNode;
  negative?: boolean;
}
function Rule({
  icon,
  children,
  negative
}: RuleProps) {
  return <div className="flex items-start gap-3" data-id="element-759">
      <div className={`mt-0.5 flex-shrink-0 ${negative ? 'text-coral' : 'text-peach'}`} data-id="element-760">
        {icon}
      </div>
      <p className="text-warmBrown/80 leading-relaxed" data-id="element-761">{children}</p>
    </div>;
}
interface CalloutProps {
  color: 'peach' | 'sage' | 'lavender' | 'coral';
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}
function Callout({
  color,
  icon,
  title,
  children
}: CalloutProps) {
  const colorMap = {
    peach: {
      bg: 'bg-peach/10',
      border: 'border-peach/20',
      text: 'text-peach'
    },
    sage: {
      bg: 'bg-sage/10',
      border: 'border-sage/20',
      text: 'text-sage'
    },
    lavender: {
      bg: 'bg-lavender/10',
      border: 'border-lavender/20',
      text: 'text-lavender'
    },
    coral: {
      bg: 'bg-coral/10',
      border: 'border-coral/20',
      text: 'text-coral'
    }
  };
  const c = colorMap[color];
  return <div className={`${c.bg} ${c.border} border rounded-2xl p-5 mt-6`} data-id="element-762">
      <div className="flex items-center gap-2 mb-2" data-id="element-763">
        <span className={c.text} data-id="element-764">{icon}</span>
        <p className={`font-bold ${c.text}`} data-id="element-765">{title}</p>
      </div>
      <p className="text-warmBrown/80 leading-relaxed text-sm" data-id="element-766">{children}</p>
    </div>;
}